import { ReactElement, useRef } from 'react';

import { AddOutlined } from '@mui/icons-material';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableRow,
} from '@mui/material';
import isEqual from 'lodash/isEqual';

import { ColumnIds } from 'enums';
import { useTableFilter } from 'hooks/common';

import { EditableRow } from './EditableRow';
import { editableRowTableStyles } from './EditableRowsTable.styles';
import { TableColumn, TableHead } from 'components/Table';

export interface EditableListColumn extends TableColumn {
  getValue?: (item: any) => string;
  editControl?: ReactElement;
}

export type EditableRowsTableProps<T> = {
  onSave: (items: Array<T & EditableItem>) => void;
  items: Array<T & EditableItem>;
  columns: Array<EditableListColumn>;
  canEditRows?: boolean;
  canSave?: boolean;
  readOnly?: boolean;
  showRows?: number;
  headerHidden?: boolean;
};

export function EditableRowsTable<T>({
  onSave,
  items,
  columns,
  canEditRows,
  canSave = true,
  readOnly,
  showRows = 5,
  headerHidden = false,
}: EditableRowsTableProps<T>) {
  const { setSearchBy, setSortBy, setFilterBy, filteredItems } = useTableFilter(items);
  const scrollRef = useRef(null);
  const oldRowData = useRef(null);

  const setInactive = () => onSave(items.filter(el => el.id).map(el => ({ ...el, active: false })));

  const handleAdd = () => {
    const row: EditableItem = { active: true, added: true, id: null };
    columns.forEach(col => (row[col.id] = ''));
    onSave([...items, { id: null, ...row } as T & EditableItem]);
    oldRowData.current = row;
    // Fire the scroll action after the render
    setTimeout(
      () =>
        scrollRef.current?.scrollBy({
          top: scrollRef.current?.scrollHeight,
          behavior: 'smooth',
        }),
      0,
    );
  };

  const handleDelete = (id: number | '') => {
    const element = items.find(el => el.id === id);
    if (element.added) {
      // Item is newly added - remove it, we do not need it
      onSave(items.filter(item => item.id !== id));
    } else {
      // Item exists - toggle its flag
      onSave(
        items.map(item => {
          if (item.id === id) {
            const clonnedItem = { ...item };
            if (clonnedItem.deleted) {
              delete clonnedItem.deleted;
            } else {
              clonnedItem.deleted = true;
            }
            return clonnedItem;
          }
          return item;
        }),
      );
    }
  };

  const handleEdit = (id: number | '') => {
    onSave(items.map(item => (item.id === id ? { ...item, active: true } : item)));
    oldRowData.current = { ...items.find(el => el.id === id) };
  };

  const handleSave = () => {
    const activeItem = items.find(el => el.active);
    if (!isEqual(oldRowData.current, activeItem)) {
      onSave(
        items.map(el =>
          // Find the active item and set it to inactive and edited
          activeItem.id === el.id ? { ...activeItem, edited: true, active: false } : el,
        ),
      );
    } else {
      setInactive();
      onSave(items.filter(el => el.id));
    }
    oldRowData.current = null;
  };

  const onCancelEdit = () => {
    const itemIdx = items.findIndex(el => el.active);
    const newItems = [...items];
    if (items[itemIdx]?.id) {
      // Item exists and has an ID, revert it
      newItems[itemIdx] = oldRowData.current;
    } else {
      // Item doesn't exist, clear it
      newItems.splice(itemIdx, 1);
    }
    onSave(newItems);
    oldRowData.current = null;
  };

  return (
    <TableContainer
      component={Paper}
      ref={scrollRef}
      sx={{ ...editableRowTableStyles.listContainer, maxHeight: (showRows + 2) * 53 }}
    >
      <Table size="small" stickyHeader>
        {!headerHidden && (
          <TableHead
            searchType="input"
            actionCell={false}
            columns={[...columns, { id: ColumnIds.Actions, label: '' }]}
            onFilterBy={value => {
              setInactive();
              setFilterBy(value);
            }}
            onRequestSort={value => {
              setInactive();
              setSortBy(value);
            }}
            onSearch={value => {
              setInactive();
              setSearchBy(value);
            }}
          />
        )}
        <TableBody>
          {filteredItems.map(item => (
            <EditableRow
              isEditable={canEditRows}
              key={item.id}
              isOnEditMode={items.some(el => el.active)}
              isSaveable={canSave && item.id != null}
              data={columns.map(({ id, editControl, getValue }) => {
                const itemValue = getValue?.(item) || item[id];
                return item.active ? editControl || itemValue : itemValue;
              })}
              itemKey={item.id}
              areActionsDisabled={readOnly || (items.some(el => el.active) && item.active !== true)} // Remove actions when another item is currently active
              onEditRow={() => handleEdit(item.id)}
              onRemoveRow={() => handleDelete(item.id)}
              onCancelEdit={onCancelEdit}
              onSaveRow={handleSave}
              added={item.added}
              deleted={item.deleted}
              edited={item.edited}
            />
          ))}
        </TableBody>
        {!readOnly && (
          <TableFooter sx={editableRowTableStyles.tableFooter}>
            <TableRow>
              <TableCell align="center" colSpan={columns.length + 1}>
                <IconButton
                  data-testid="add-relation"
                  onClick={handleAdd}
                  size="small"
                  disabled={items.some(el => el.active) || items.length !== filteredItems.length}
                >
                  <AddOutlined />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
}
